import { QuestionCircleOutlined } from '@ant-design/icons'
import { colors } from '@lmc/common/constants'
import { FormCard } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { InputNumber, Switch } from 'antd'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { useEffect, useState } from 'react'
import { type TFunction, useTranslation } from 'react-i18next'
import styled from 'styled-components'

interface FluidTrackingSectionProps {
  isEnabled: boolean
  intakeTarget: number | string
  onEnabledChange: (enabled: boolean) => void
  onIntakeTargetChange: (target: number | string) => void
  showValidationError?: boolean
}

const FluidTrackingSection = ({
  isEnabled,
  intakeTarget,
  onEnabledChange,
  onIntakeTargetChange,
  showValidationError = false
}: FluidTrackingSectionProps) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)

  const [validationError, setValidationError] = useState('')
  const [isTouched, setIsTouched] = useState(false)

  const validateInput = (value: number | string | null) => {
    if (value === null || value === undefined || value === '') {
      return isTouched || showValidationError
        ? t(carePlansConstants.ERROR_FLUID_TARGET_CAN_NOT_BE_EMPTY)
        : ''
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value

    if (typeof numValue === 'number' && isNaN(numValue)) {
      return isTouched || showValidationError
        ? t(carePlansConstants.ERROR_FLUID_TARGET_CAN_NOT_BE_EMPTY)
        : ''
    }

    if (numValue < 0) {
      return t(carePlansConstants.ERROR_INVALID_CHARACTER)
    }
    if (numValue === 0) {
      return t(carePlansConstants.ERROR_FLUID_TARGET_CAN_NOT_BE_0)
    }
    if (numValue > 9999) {
      return t(carePlansConstants.ERROR_CORRECT_AMOUNT)
    }
    return ''
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (showValidationError) {
      const error = validateInput(intakeTarget)
      setValidationError(error)
    }
  }, [showValidationError, intakeTarget])

  const handleInputChange = (value: number | string | null) => {
    setIsTouched(true)
    const error = validateInput(value)
    setValidationError(error)

    if (value === null || value === undefined || value === '') {
      onIntakeTargetChange('')
    } else {
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      if (typeof numValue === 'number' && !isNaN(numValue)) {
        onIntakeTargetChange(numValue)
      } else {
        onIntakeTargetChange('')
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/[0-9]/.test(e.key) &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
    ) {
      e.preventDefault()
    }
  }

  return (
    <>
      {!isEnabled ? (
        <FormCard
          title={
            <TitleContainer>
              <FormCardHeading level={3}>
                {t(carePlansConstants.FLUID_TRACKING)}
              </FormCardHeading>
              {/* <HelpLinkComponent t={t} /> */}
            </TitleContainer>
          }
          rightChildren={
            <SwitchComponent
              onEnabledChange={onEnabledChange}
              isEnabled={isEnabled}
              t={t}
            />
          }
        />
      ) : (
        <FormCard
          title={t(carePlansConstants.FLUID_TRACKING)}
          rightChildren={
            <SwitchComponent
              onEnabledChange={onEnabledChange}
              isEnabled={isEnabled}
              t={t}
            />
          }
        >
          <InputContainer>
            <InputLabel>Daily intake target</InputLabel>
            <IntakeInput
              value={intakeTarget}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onBlur={() => {
                setIsTouched(true)
                const error = validateInput(intakeTarget)
                setValidationError(error)
              }}
              addonAfter="ml"
              status={validationError ? 'error' : ''}
              step={100}
              min={0}
            />
            {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
          </InputContainer>
          {/* <HelpLinkComponent t={t}/> */}
        </FormCard>
      )}
    </>
  )
}

const SwitchComponent = ({
  isEnabled,
  onEnabledChange,
  t
}: {
  isEnabled: boolean
  onEnabledChange: (checked: boolean) => void
  t: TFunction
}) => (
  <SwitchContainer>
    <SwitchLabel>
      {isEnabled ? t(carePlansConstants.DISABLE) : t(carePlansConstants.ENABLE)}
    </SwitchLabel>
    <Switch
      checked={isEnabled}
      onChange={(checked) => {
        onEnabledChange(checked)
      }}
    />
  </SwitchContainer>
)

const HelpLinkComponent = ({ t }: { t: TFunction }) => (
  <HelpLinkContainer>
    <HelpLink
      href="#"
      target="_blank"
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()
      }}
    >
      {t(carePlansConstants.FIND_OUT_MORE)}
    </HelpLink>
    <HelpIcon />
  </HelpLinkContainer>
)

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const FormCardHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
`

const SwitchLabel = styled(Text)`
  margin-right: 10px;
`

const InputContainer = styled.div`
  margin-bottom: 20px;
`

const InputLabel = styled(Text)`
  margin-bottom: 8px;
  display: block;
`

const IntakeInput = styled(InputNumber)`
  width: 165px;
`

const HelpLinkContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

const HelpLink = styled.a`
  margin-right: 8px;
  text-decoration: none;
`

const HelpIcon = styled(QuestionCircleOutlined)`
  color: ${(props) => props.theme.color.status.primary};
  font-size: 16px;
`

const ErrorMessage = styled(Text)`
  color: ${colors.red};
  font-size: 12px;
  margin-top: 4px;
  display: block;
`

export default FluidTrackingSection
